import api from './api'
import { buildEndpoint } from '../utils/apiUtils'

export interface SettingItem {
  key: string
  value: string | null
}

export interface DesignSettings {
  primaryColor?: string
  secondaryColor?: string
  faviconUrl?: string
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string
  restaurantName?: string
  headerLogo?: string
  footerLogo?: string
}

type SettingsMap = Record<string, string | null>

function toSettingsMap(collection: SettingItem[]): SettingsMap {
  return collection.reduce<SettingsMap>((acc, item) => {
    acc[item.key] = item.value
    return acc
  }, {})
}

let settingsCollectionPromise: Promise<SettingItem[]> | null = null

export async function fetchSettingsCollection(): Promise<SettingItem[]> {
  if (!settingsCollectionPromise) {
    settingsCollectionPromise = api
      .get(buildEndpoint('/cashier/settings'))
      .then((res) => (res.data?.data?.collection ?? []) as SettingItem[])
      .catch(() => [] as SettingItem[])
  }

  return settingsCollectionPromise
}

async function fetchDecimalSetting(key: string): Promise<number | null> {
  const collection = await fetchSettingsCollection()
  const raw = collection.find((item) => item.key === key)?.value
  if (raw == null || raw === '') return null
  const value = parseFloat(raw)
  return Number.isFinite(value) ? value : null
}

export function fetchOrderTaxPercentage(): Promise<number | null> {
  return fetchDecimalSetting('order_tax_perc')
}

export function fetchOrderServiceFeePercentage(): Promise<number | null> {
  return fetchDecimalSetting('order_service_fee_perc')
}

// Module-level cache so UI can read settings that arrived from API
let cachedDesign: DesignSettings = {}

export function getCurrentDesign(): DesignSettings {
  return cachedDesign
}

function setCurrentDesign(design: DesignSettings) {
  cachedDesign = design
  // Expose for quick debugging or non-React consumers
  ;(window as any).__designSettings = design
}

export async function fetchDesignSettings(): Promise<DesignSettings> {
  try {
    const data = await fetchSettingsCollection()
    const map = toSettingsMap(data)

    const design: DesignSettings = {
      primaryColor: (map['primary_color'] as string) || undefined,
      secondaryColor: (map['secondary_color'] as string) || undefined,
      faviconUrl: (map['fav_icon'] as string) || undefined,
      metaTitle: (map['meta_title'] as string) || undefined,
      metaDescription: (map['meta_description'] as string) || undefined,
      metaKeywords: (map['meta_keywords'] as string) || undefined,
      restaurantName: (map['restaurant_name'] as string) || undefined,
      headerLogo: (map['header_logo'] as string) || undefined,
      footerLogo: (map['footer_logo'] as string) || undefined,
    }

    setCurrentDesign(design)
    return design
  } catch (e) {
    // Swallow error and return empty design to avoid blocking app
    return {}
  }
}

export function applyDesignSettings(design: DesignSettings): void {
  // Apply colors via CSS variables
  if (design.primaryColor) {
    document.documentElement.style.setProperty('--brand-primary', design.primaryColor)
  }
  if (design.secondaryColor) {
    document.documentElement.style.setProperty('--brand-secondary', design.secondaryColor)
  }

  // Apply document title (prefer metaTitle, fallback to restaurantName)
  const title = design.metaTitle?.trim() || design.restaurantName?.trim()
  if (title) {
    document.title = title
  }

  // Apply favicon
  if (design.faviconUrl) {
    const linkRelIconSelectors = [
      'link[rel="icon"]',
      'link[rel="shortcut icon"]',
      'link[rel="apple-touch-icon"]',
    ]
    let link: HTMLLinkElement | null = null
    for (const sel of linkRelIconSelectors) {
      const found = document.querySelector(sel) as HTMLLinkElement | null
      if (found) {
        link = found
        break
      }
    }
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = design.faviconUrl
  }

  // Apply meta description
  if (design.metaDescription) {
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'description'
      document.head.appendChild(meta)
    }
    meta.content = design.metaDescription
  }

  // Apply meta keywords
  if (design.metaKeywords) {
    let meta = document.querySelector('meta[name="keywords"]') as HTMLMetaElement | null
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'keywords'
      document.head.appendChild(meta)
    }
    meta.content = design.metaKeywords
  }

  // Update cache and notify listeners that branding is ready
  setCurrentDesign(design)
  window.dispatchEvent(new CustomEvent('designSettingsApplied', { detail: design }))
}
